import { Icon, Icons, List, ListItem } from '@silverfort/sf-components';
import InsightBanner from '../components/InsightBanner';
import DetailedProgressBar from '../components/DetailedProgressBar';
import { PRIVILEGED_ACCOUNTS_FILTERS, PRIVILEGED_ACCOUNTS_STATUS } from '@silverfort-nodelibs/common/utils/consts';
import { usePrivilegedAccountContext } from '../context/PrivilegedAccountsContext';

const PrivilegedAccountsInsights = ({ statistics, selectedFilters, setSelectedFilters, isEditor }) => {
    const { jitSynced, rulesSyncStatus } = usePrivilegedAccountContext();

    const statusBar = (
        <DetailedProgressBar
            current={statistics.disabled}
            total={statistics.total}
            description={'Accounts currently disabled in Active Directory by JIT policy'}
            fillClassName={'bg-sf-positive-500'}
        />
    );

    const protectedBar = (
        <DetailedProgressBar
            current={statistics.protected}
            total={statistics.total}
            synced={jitSynced && rulesSyncStatus}
            description={'Accounts protected with JIT or Virtual Fencing policies'}
            fillClassName={'bg-sf-positive-500'}
        />
    );

    const tier0To1Bar = (
        <DetailedProgressBar
            current={statistics.cross_tier01}
            description={
                <span className="h-4 flex items-center">
                    0 <Icon size={14} className="text-sf-gray-400" icon={Icons.ArrowForward} /> 1
                </span>
            }
            fillClassName={'bg-sf-negative-500'}
        />
    );

    const tier0To2Bar = (
        <DetailedProgressBar
            current={statistics.cross_tier02}
            description={
                <span className="h-4 flex items-center">
                    0 <Icon className="h-4 w-4 text-sf-gray-300 border-box" icon={Icons.ArrowForward} /> 2
                </span>
            }
            fillClassName={'bg-sf-negative-300'}
        />
    );

    const tier1To2Bar = (
        <DetailedProgressBar
            current={statistics.cross_tier12}
            description={
                <span className="h-4 flex items-center">
                    1 <Icon className="h-4 w-4 text-sf-gray-300 border-box" icon={Icons.ArrowForward} /> 2
                </span>
            }
            fillClassName={'bg-sf-warning-500'}
        />
    );

    const navigateToResourceSettings = () => {
        window.open('/Settings/Privileged Access#tiersConfig', '_blank');
    };

    return (
        <div className="flex w-full items-center gap-4">
            <InsightBanner
                menuContent={
                    <List className="p-[0]">
                        <ListItem
                            title="Apply as filter"
                            onClick={() =>
                                setSelectedFilters({
                                    ...selectedFilters,
                                    [PRIVILEGED_ACCOUNTS_FILTERS.status.id]: [PRIVILEGED_ACCOUNTS_STATUS.Disabled],
                                    [PRIVILEGED_ACCOUNTS_FILTERS.policy.id]: ['jit']
                                })
                            }
                        />
                    </List>
                }
                className={'w-full'}
                dataElements={[statusBar]}
                icon={Icons.GaugeChart}
                title="Account Status"
            />
            <InsightBanner
                menuContent={
                    <List className="p-[0]">
                        <ListItem
                            title="Apply as filter"
                            onClick={() =>
                                setSelectedFilters({
                                    ...selectedFilters,
                                    [PRIVILEGED_ACCOUNTS_FILTERS.policy.id]: ['jit', 'fencing']
                                })
                            }
                        />
                    </List>
                }
                className={'w-full'}
                dataElements={[protectedBar]}
                icon={Icons.ShieldCheck}
                title="Protected Privileged Accounts"
            />
            <InsightBanner
                menuContent={
                    <List className="p-[0]">
                        <ListItem
                            title="Apply as filter"
                            onClick={() =>
                                setSelectedFilters({
                                    ...selectedFilters,
                                    [PRIVILEGED_ACCOUNTS_FILTERS.crossTier.id]: ['>', 0]
                                })
                            }
                        />
                        {isEditor && (
                            <ListItem
                                title="Resource Settings"
                                IconRight={Icons.OpenInNew}
                                onClick={navigateToResourceSettings}
                            />
                        )}
                    </List>
                }
                className={'w-full'}
                dataElements={[tier0To1Bar, tier0To2Bar, tier1To2Bar]}
                icon={Icons.ShieldBroken}
                title="Cross Tier Authentication"
                info="Authentication to unclassified servers are filtered but not counted"
            />
        </div>
    );
};

export default PrivilegedAccountsInsights;
